import { Grid, Box, Typography, IconButton, TextField } from "@mui/material";
import { t } from "i18next";
import React, { useEffect, useState, useRef } from "react";
import { List, OnChangeMeta, arrayMove } from "react-movable";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import StarsIcon from "@mui/icons-material/Stars";
import DeleteIcon from "@mui/icons-material/Delete";
import { SelectedProductListProps } from "../../../interfaces/productVariationInterface";
import Utility from "../../../utils/Utility";
import Skeleton from "../../common/Skeleton";
import ConfirmationModal from "../../common/ConfirmationModal";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useAppSelector } from "../../../redux/hooks";
import ImageIcon from "@mui/icons-material/Image";

type VariationForm = {
    products: Array<{
        cainzProductCode: string;
        postingStatus: number;
        variations: Array<{
            variationType: string;
            variationValue: string;
        }>;
    }>;
};

const SelectedProductList = (props: SelectedProductListProps) => {
    const { 
        selectedProducts,
        previousProducts, 
        setSelectedProducts, 
        showSnackbar,
        checkedVariationType,
        setProductVariationPayload, 
        productVariationPayload, 
        showSkeleton,
        validator,
        setFieldErrors, 
        variations, 
        setVariations,
        setCheckedVariationType,
        initialVariationTypes,
        isEditMode,
        setCompletedDataSet
    } = props;
    
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [deleteCainzProductCode, setDeleteCainzProductCode] = useState<string | null>(null);
    const [duplicateSet, setDuplicateSet] = useState<Record<string, string[]>>({});
    const [isTyping, setIsTyping] = useState(false);
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isDemoUser = useAppSelector((state) => state.common.demoUser) ?? false;
    const isSuperAdminDemoSeller = useAppSelector((state) => state.common.superAdminDemoSeller) ?? false;


    const visibleProducts = selectedProducts.filter((product) => product.postingStatus !== 9);

    const {
        control,
        getValues,
        trigger,
        reset,
        formState
    } = useForm<VariationForm>({
        mode: "onBlur", // Changed to onBlur
        shouldUnregister: false,
        defaultValues: { products: [] },
    });

    // Run checkForDuplicate whenever a field is blurred
    useEffect(() => {
        if (formState.touchedFields.products) {
            const products = getValues("products");
            if (products.length > 0) {
                checkForDuplicate(products);
            }
        }
    }, [formState.touchedFields]);

    useEffect(() => {
        const products = getValues("products");
        
        if (products.length > 0) {
            setCompletedDataSet(products);
        }

    }, [getValues("products"), setCompletedDataSet]);

    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, []);


    useEffect(()=>{        
        if(!isTyping) {
            const products = getValues("products");
            if (products.length > 0) {
                setProductVariationPayload((prev)=> ({
                    ...prev,
                    productVariations : products.map((product) => ({
                        cainzProductCode: product.cainzProductCode,
                        postingStatus: product.postingStatus ,
                        variations: product.variations.map((variation,varIndex) =>{
                            const indexValue = variations.findIndex((v) => v.name === variation.variationType);
                            const isListDropDown = variations[indexValue]?.checked && variations[indexValue]?.type === "List Drop-down";
                            const productWithDeleteIndex = prev.productVariations.filter((p) => p.postingStatus ==9).findIndex((p)=> p.cainzProductCode === product.cainzProductCode);
                            const deletedProduct = prev.productVariations.filter((p) => p.postingStatus ==9)[productWithDeleteIndex];
                            return{
                                variationType: variation.variationType,
                                variationValue: variation.variationValue,
                                displayType: product.postingStatus === 9 ? deletedProduct.variations[varIndex].displayType:isListDropDown ? "4":"3",
                                displayOrder: 1
                            };
                        }
                        )
                    }))
                }));
            
            }
        }
    },[isTyping]);
    
    const { fields } = useFieldArray({
        control,
        name: "products",
    });

    useEffect(() => {
        if (!productVariationPayload?.productVariations?.length) return;

        const mapped = productVariationPayload.productVariations.map((product) => {
        // Get current form values if they exist
            const currentFormValues = getValues("products") || [];
            const currentProductValues = currentFormValues.find(p => 
                p.cainzProductCode === product.cainzProductCode
            );

            // For deleted products, preserve existing variations
            if (product.postingStatus === 9) {
                return {
                    cainzProductCode: product.cainzProductCode,
                    postingStatus: product.postingStatus,
                    variations: product.variations || [] // Ensure variations array exists
                };
            }

            return {
                cainzProductCode: product.cainzProductCode,
                postingStatus: product.postingStatus,
                variations: checkedVariationType.map((variationType) => {
                // Try to find value in current form first
                    const currentVariation = currentProductValues?.variations?.find(v => 
                        v.variationType === variationType
                    );
        
                    // Fall back to payload if no current form value
                    if (currentVariation) {
                        return currentVariation;
                    }

                    const matched = product.variations?.find((v) => 
                        v.variationType?.normalize("NFKC").toLowerCase() === variationType?.normalize("NFKC").toLowerCase()
                    );
        
                    return {
                        variationType,
                        variationValue: matched?.variationValue ,
                    };
                }),
            };
        });

        reset({ products: mapped });
    }, [productVariationPayload, checkedVariationType, reset]);

    // Check for empty product list and reset variations
    useEffect(() => {
        if(visibleProducts.length === 0 && checkedVariationType.length > 0){
            
            setVariations(initialVariationTypes);
            setCheckedVariationType([]);
            setSelectedProducts([]);
        }
    }, [visibleProducts.length, checkedVariationType.length]);

    // Update field errors when dependencies change
    useEffect(() => {
        const products = getValues("products");
        if (products.length > 0) {
            checkForDuplicate(products);
        }
        validator.current.showMessages();
    }, [selectedProducts, checkedVariationType, productVariationPayload, variations]);


    // Add this helper function at the top of your component
    const getStableKey = (product: any) => `${product.cainzProductCode}-${product.postingStatus}`;

    // Updated handleDelete function
    // Modify the handleDelete function to preserve variations for previous products
    const handleDelete = (deleteCode: string): void => {
        if (!deleteCode) return;

        const isPreviousProduct = previousProducts.some(p => p.cainzProductCode === deleteCode);

        // Clean up fieldErrors for the deleted product
        setFieldErrors((prevErrors: any) => {
            const newErrors = { ...prevErrors };
            
            // Remove errors by product code instead of index
            checkedVariationType.forEach((variationType) => {
                const normalizedType = variationType.trim().replace(/\s+/g, " ").normalize("NFKC").toLowerCase();
                Object.keys(newErrors).forEach(key => {
                    
                    if (key.includes(`-${deleteCode}-${normalizedType}`)) {
                        delete newErrors[key];
                    }
                });
            });
            return newErrors;
        });

        // Update selectedProducts
        setSelectedProducts((prev) => {
            const updated = [...prev];
            const idx = updated.findIndex((item) => item.cainzProductCode === deleteCode);
            const productToDelete = updated[idx];

            if (isPreviousProduct) {
                updated[idx] = {
                    ...productToDelete,
                    postingStatus: 9,
                };
            } else {
                updated.splice(idx, 1);
            }

            showSnackbar(t("product_variation_list.product_delete"), true);
            return updated;
        });

        if (!isPreviousProduct) {

            const currentFormValues = getValues("products") || [];
            const updatedFormValues = currentFormValues.filter(p => p.cainzProductCode !== deleteCode);
            reset({ products: updatedFormValues });
            setCompletedDataSet((prev: any) => prev.filter((item: any) => item.cainzProductCode !== deleteCode));
            setProductVariationPayload((prev) => ({
                ...prev,
                productVariations: prev.productVariations.filter(p => p.cainzProductCode !== deleteCode),
            }));
        } else {
        // 🧾 Previous product: mark deleted, keep variationTypes
            const deletedProduct = previousProducts.find(p => p.cainzProductCode === deleteCode);

            const currentFormValues = getValues("products") || [];
            const updatedFormValues = currentFormValues.map(product => {
                if (product.cainzProductCode !== deleteCode) return product;
                return {
                    ...product,
                    postingStatus: 9,
                    variations: deletedProduct?.variationTypes // Keep existing variations
                };
            });
            reset({ products: updatedFormValues });

            setProductVariationPayload((prev) => {
                const updatedVariations = prev.productVariations.map(product => {
                    if (product.cainzProductCode !== deleteCode) return product;
                    
                    return {
                        ...product,
                        postingStatus: 9,
                        variations: deletedProduct?.variationTypes || [] // Keep existing variations
                    };
                });


                return {
                    ...prev,
                    productVariations: updatedVariations
                };
            });
        }

        setDeleteCainzProductCode(null);
    };

    const handleDeleteClick = (cainzProductCode: string): void => {
        setDeleteCainzProductCode(cainzProductCode);
        setShowDeleteModal(true);
    };

    const confirmDelete = (): void => {
        if (deleteCainzProductCode !== null) {
            handleDelete(deleteCainzProductCode);
        }
        setShowDeleteModal(false);
    };

    const cancelDelete = (): void => {
        setShowDeleteModal(false);
        setDeleteCainzProductCode(null);
    };

    // Handle drag and drop reordering
    const handleDragEnd = (event: OnChangeMeta): void => {
        const { oldIndex, newIndex } = event;
        const reorderedVisible = arrayMove(visibleProducts, oldIndex, newIndex);
        const hidden = selectedProducts.filter(p => p.postingStatus === 9);
        const newSelectedProducts = [...reorderedVisible, ...hidden];
        
        // Update selected products
        setSelectedProducts(newSelectedProducts);
        reset({products : newSelectedProducts});

        setCompletedDataSet((prev :any)=> {
            const visibleData = prev.filter((p : any) => p.postingStatus !== 9);
            const hiddenData = prev.filter((p:any) => p.postingStatus === 9);
            const reorderedData = arrayMove(visibleData, oldIndex, newIndex);
            return [...reorderedData, ...hiddenData];
        });

        setProductVariationPayload((prev) => {
            const visibleData = prev.productVariations.filter((p: any) => p.postingStatus !== 9);
            const hiddenData = prev.productVariations.filter((p: any) => p.postingStatus === 9);
            const reorderedData = arrayMove(visibleData, oldIndex, newIndex);
            return {
                ...prev,
                productVariations: [...reorderedData, ...hiddenData]
            };
        });

        showSnackbar(t("product_variation_list.products_reordered"), true);
    }; 

    const checkForDuplicate = (products: any[]): void => {
        if (!products || products.length === 0) {
            setDuplicateSet({});
            setFieldErrors({});
            return;
        }

        const newFieldErrors: Record<string, boolean> = {};
        const duplicateMap: Record<string, Set<string>> = {};

        // Create a map of variation values for each product
        const productVariationMap = new Map<string, Map<string, string>>();

        products.forEach(product => {
            if (product.postingStatus === 9) return;
    
            const variationMap = new Map<string, string>();
            let hasAllValues = true;
        
            // Check if this product has values for ALL variation types
            product.variations?.forEach((v: any) => {
                if (v.variationType) {
                    const normalizedType = v.variationType.trim().normalize("NFKC").toLowerCase();
                    if (v.variationValue && v.variationValue.trim() !== "") {
                        const normalizedValue = v.variationValue.trim().replace(/\s+/g, " ").normalize("NFKC").toLowerCase();
                        variationMap.set(normalizedType, normalizedValue);
                    } else {
                    // If any variation value is empty, mark this product as incomplete
                        hasAllValues = false;
                    }
                }
            });
        
            // Only add products to comparison if they have ALL variation values filled
            if (hasAllValues && variationMap.size > 0) {
                productVariationMap.set(product.cainzProductCode, variationMap);
            }
        });

        const productCodes = Array.from(productVariationMap.keys());

        for (let i = 0; i < productCodes.length; i++) {
            const firstProductCode = productCodes[i];
            const firstVariation = productVariationMap.get(firstProductCode)!;
    
            for (let j = i + 1; j < productCodes.length; j++) {
                const secondProductCode = productCodes[j];
                const secondVariation = productVariationMap.get(secondProductCode)!;
        
                let isDuplicate = true;
                const hasAllValues = true;
            
                // Skip comparison if either product has no variation values
                if (firstVariation.size === 0 || secondVariation.size === 0) {
                    continue;
                }
            
                // Get all variation types that should be compared (from checkedVariationType)
                const firstKeys = Array.from(firstVariation.keys());
                const secondKeys = Array.from(secondVariation.keys());
                const allVariationTypes: string[] = [];
            
                // Add unique keys from both variations
                firstKeys.forEach(key => {
                    if (allVariationTypes.indexOf(key) === -1) {
                        allVariationTypes.push(key);
                    }
                });
                secondKeys.forEach(key => {
                    if (allVariationTypes.indexOf(key) === -1) {
                        allVariationTypes.push(key);
                    }
                });
        
                // Check if both products have values for ALL required variation types
                // If either product has any empty values, skip duplicate check entirely
                let firstProductHasAllValues = true;
                let secondProductHasAllValues = true;
            
                for (let k = 0; k < allVariationTypes.length; k++) {
                    const type = allVariationTypes[k];
                    const firstValue = firstVariation.get(type);
                    const secondValue = secondVariation.get(type);
                
                    if (!firstValue || firstValue.trim() === "") {
                        firstProductHasAllValues = false;
                    }
                    if (!secondValue || secondValue.trim() === "") {
                        secondProductHasAllValues = false;
                    }
                }
            
                // Skip duplicate check if either product has empty values
                if (!firstProductHasAllValues || !secondProductHasAllValues) {
                    continue;
                }
            
                // Now check if all variation values match (only for products with complete data)
                for (let k = 0; k < allVariationTypes.length; k++) {
                    const type = allVariationTypes[k];
                    const firstValue = firstVariation.get(type);
                    const secondValue = secondVariation.get(type);
            
                    if (firstValue !== secondValue) {
                        isDuplicate = false;
                        break;
                    }
                }
        
                // Only mark as duplicate if all values match AND both products have complete data
                if (isDuplicate && allVariationTypes.length > 0) {
                // Add both products to each other's duplicate sets
                    if (!duplicateMap[firstProductCode]) duplicateMap[firstProductCode] = new Set();
                    if (!duplicateMap[secondProductCode]) duplicateMap[secondProductCode] = new Set();
            
                    duplicateMap[firstProductCode].add(secondProductCode);
                    duplicateMap[secondProductCode].add(firstProductCode);
            
                    // Mark errors for both products
                    firstVariation.forEach((_, type) => {
                        newFieldErrors[`variationValue-${firstProductCode}-${type?.trim()}`] = true;
                    });
            
                    secondVariation.forEach((_, type) => {
                        newFieldErrors[`variationValue-${secondProductCode}-${type?.trim()}`] = true;
                    });
                }
            }
        }

        // Convert Sets to arrays
        const finalDuplicateSet: Record<string, string[]> = {};
        Object.entries(duplicateMap).forEach(([key, valueSet]) => {
            finalDuplicateSet[key] = Array.from(valueSet);
        });

        setDuplicateSet(finalDuplicateSet);
        setFieldErrors(newFieldErrors);
    };

    const isDuplicateCode = (code: string): boolean => {
        if (duplicateSet[code]) return true;
        return Object.values(duplicateSet).some(arr => arr.includes(code));
    };

    // Helper function to normalize variation types
    const normalizeVariationType = (val: string): string => val.trim().replace(/\s+/g, " ");

    return (
        <Grid>
            {
                visibleProducts?.length > 0 &&
                <Grid container className="word_wrap_class">
                    <Grid item xs={12} sm={3} md={1} lg={1} justifyContent={{sm:"center", xs:"left"}} display="flex">
                        <Typography className="main_grid_data">{t("product_variation_list.sno")} </Typography>
                    </Grid>
                    <Grid item xs={12} sm={5} md={2} lg={2}>
                        <Typography className="main_grid_data">{t("product_variation_list.featured_image")} </Typography>
                    </Grid>
                    <Grid item xs={12} sm={2} md={2} lg={2}>
                        <Typography className="main_grid_data">{t("product_variation_list.product_code")}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={2} md={2} lg={2}ml={3}>
                        <Typography className="main_grid_data">{t("product_variation_list.cainz_product_code")}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={3} lg={2} mr={3}>
                        <Typography className="main_grid_data">{t("product_variation_list.product_name")}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={2} md={1} lg={1}>
                        <Typography className="main_grid_data">{t("product_variation_list.public_flag")}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={2} md={1} lg={1} sx={{ display: "flex", justifyContent: {md:"flex-end", sm:"flex-start"}}}>
                        <Typography className="main_grid_data">{t("product_variation_list.action")}</Typography>
                    </Grid>
                </Grid>
            }
            
            {showSkeleton ? (
                <Skeleton count={5} my={1} variant="rectangular" height={50} />
            ) : visibleProducts?.length > 0 ? (
                <List
                    values={visibleProducts}
                    onChange={handleDragEnd}
                    renderList={({ children, props }) => <Grid container {...props}>{children}</Grid>}
                    renderItem={({ value, props, index }: any) => { 
                        
                        // Find the corresponding form index
                        const formIndex = getValues("products")?.findIndex(
                            p => p.cainzProductCode === value.cainzProductCode
                        );
                        return(
                            <Grid
                                container
                                {...props}
                                key={getStableKey(value)}
                                borderBottom="1px solid gray"
                                marginTop={1}
                                alignItems="center"
                                boxShadow="0px 1px #AAA"
                            >
                                <Grid item xs={12} sm={3} md={1} lg={1}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Box sx={{ mr: 1, cursor: "grab" }} data-movable-handle>
                                            <DragIndicatorIcon />
                                        </Box>
                                        <Typography sx={{ml:1}}>{index + 1}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={5} md={2} lg={2} justifyContent="center">
                                    { value?.featuredImage != "" ? 
                                        <Box sx={{ width: 55, height: 55 }}>
                                            <img
                                                src={(isDemoUser || isSuperAdminDemoSeller) ? `${value?.featuredImage}` : `${value?.featuredImage}?date=${new Utility().dateTimeCache()}`}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "contain",
                                                }}
                                                alt=""
                                            />
                                        </Box>:
                                        <ImageIcon className="featuredImage" />
                                    }
                                </Grid>
                                <Grid item xs={12} sm={2} md={2} lg={2} sx={{userSelect: "text !important",wordBreak: "break-all"}} >
                                    <Typography>{value?.productCode}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={2} md={2} lg={2} sx={{userSelect: "text !important"}}ml={3}>
                                    <Typography>{value?.cainzProductCode}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={8} md={3} lg={2} sx={{ wordBreak: "break-all", userSelect: "text !important" }} mr={3}>
                                    <Typography>{value?.productName}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={2} md={1} lg={1}>
                                    <Typography>
                                        {value?.publicFlag == 0 ? t("product_variation_list.private") : t("product_variation_list.public")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={2} md={1} lg={1} justifyContent={{md:"flex-end", sm:"flex-start"}} display="flex" alignItems="center">
                                    {index === 0 && <StarsIcon />}
                                    <IconButton onClick={() => handleDeleteClick(value.cainzProductCode)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid>
                            
                                <Grid container spacing={2} mb={3} mt="1px">
                                    <Grid item lg={1} display={{lg:"flex", xs:"none"}}></Grid>
                                    {checkedVariationType.map((val, varIndex) => {
                                        const normalizedVal = normalizeVariationType(val);
                                        return (
                                            <Grid 
                                                item 
                                                xs={12} 
                                                sm={6} 
                                                md={6} 
                                                lg={4} 
                                                key={val} 
                                                display="flex" 
                                                justifyContent={{xs:"flex-start", sm:"space-between"}}
                                            >
                                                <Box display="flex" flexDirection="column">
                                                    <Box display="flex" alignItems="center">
                                                        <Typography mr={2} width={{xs:145, sm:"auto"}}>{val}</Typography>
                                                        <Box>
                                                            <Controller
                                                                name={`products.${formIndex}.variations.${varIndex}.variationValue`}
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <>
                                                                        <TextField
                                                                            {...field}
                                                                            size="small"
                                                                            fullWidth
                                                                            onChange={(e) => {
                                                                                field.onChange(e);
                                                                                // Removed checkForDuplicate call
                                                                                trigger();
                                                                                // Mark typing as active
                                                                                setIsTyping(true);
                                                                                // Reset previous timer
                                                                                if (typingTimeoutRef.current) {
                                                                                    clearTimeout(typingTimeoutRef.current);
                                                                                }
                                                                                // Set timer to mark "not typing" after a delay
                                                                                typingTimeoutRef.current = setTimeout(() => {
                                                                                    setIsTyping(false);
                                                                                }, 700); // <-- Adjust debounce delay as needed
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                // Handle onBlur event
                                                                                field.onBlur();
                                                                                // Check for duplicates on blur
                                                                                const products = getValues("products");
                                                                                if (products.length > 0) {
                                                                                    checkForDuplicate(products);
                                                                                }
                                                                            }}
                                                                        />
                                                                        <Box>
                                                                            {(() => {
                                                                                const indexValue = variations.findIndex((v) => v.name === val);
                                                                                const isListDropDown = indexValue >= 0 && 
                                                                                            variations[indexValue]?.checked && 
                                                                                            variations[indexValue]?.type === "List Drop-down";
                                                                                const validatorName = isListDropDown ? "maxChar50Validation" : "maxChar15Validation";
                                                                                const maxLength = isListDropDown ? 50 : 15;
                                                                        
                                                                                return validator.current.message(
                                                                                    `variationValue-${value.cainzProductCode}-${normalizedVal}`,
                                                                                    field.value,
                                                                                    `${validatorName}:${maxLength}`
                                                                                );
                                                                            })()}
                                                                        </Box>
                                                                        <Box>
                                                                            {isDuplicateCode(value.cainzProductCode) && (
                                                                                <Box sx={{wordBreak: "break-all", color: "red"}}>
                                                                                    {t("product_variation_list.duplicate_variation_combination")}
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                        
                                                                    </>
                                                                )}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Grid>
                        );}}
                />
            ) : (
                <Grid container className="no_data_found">
                    <Grid>
                        <Typography sx={{ fontWeight: "500" }} className="create_variation">
                            {t("product_variation_list.create_variation")}
                        </Typography>
                    </Grid>
                </Grid>
            )}
            
            <ConfirmationModal
                open={showDeleteModal}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                title=""
                message={
                    <Box style={{display: "flex", flexDirection: "column", color:"black", textAlign: "left"}}>
                        <Box>{t("product_variation_list.confirm_message_delete_variation")}</Box>
                        {isEditMode ? 
                            <Box>{t("product_variation_list.confirm_message_actual_reflection_update")}</Box> :
                            <Box>{t("product_variation_list.confirm_message_actual_reflection_save")}</Box>
                        }
                    </Box>
                }
                confirmText={t("product_variation_list.yes")}
                cancelText={t("product_variation_list.no")}
            />
        </Grid>
    );
};

export default SelectedProductList;